<script setup lang="ts">
/*
 * Project management — the entry point of the price list. With no project open this IS the page
 * (see Cart.vue), so everything starts here: a Wilo template, a custom price list, or one of the
 * user's saved projects.
 *
 * Two sources, deliberately kept apart on screen, own projects on top (13.10.2026, Christoph):
 *   My Projects     CART_LIST for this account and this cart type; the server (SQLite cartStorage)
 *                   is the only place they live. Closed by the "Custom price list" tile, which
 *                   reaches the server the moment its name is confirmed.
 *   Wilo Templates  the predefined catalogs (maintained in CMI, exported by scripting-wilo). A
 *                   template reaches the server only with "Enable edit".
 *
 * A project opened from here is handed up to Cart.vue, which then shows the list instead of this
 * screen. Nothing is stored locally: the price-list cart is emptied on every page load.
 */
import type * as pfWilo from '@amirada/lib-wilo-shared'
import type { IPriceListNode } from '@amirada/lib-wilo-shared'
import { ExtCmdType } from '@amirada/lib-wilo-shared'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ut_getText } from '@base/utils/utils'
import type { ECarts } from '@base/utils/constants'
import { useCartStore } from '@base/stores/cartStore'
import type { IOpenProject } from '~/utils/types'
import { ut_wilo_accountId } from '~/utils/utils'
import { ut_wilo_coverTilePicture, ut_wilo_templateText } from '~/utils/priceList'
import { usePfinderAPI } from '~/composables/pfinderAPI'
import { usePriceListTemplate } from '~/composables/priceListTemplate'
import { useProjectRename } from '~/composables/projectAutoSave'

type TCartListItem = pfWilo.ExtCmd<ExtCmdType.CART_LIST>['res'][number]

const p = defineProps<{
  cart: ECarts
  // Cart.vue is still opening a project (loading a saved one, creating a custom one).
  opening: boolean
}>()

/*
 * ⚑ `release` comes FIRST on every way in, before the cart is touched — see `usePriceListProject#releaseProject`
 * for why. A saved project is only asked for (`openSaved`): Cart.vue loads it itself, because this
 * component can be gone before the load answers (the browser's forward button) and an `open` sent
 * from here afterwards would reach nobody.
 */
const emit = defineEmits<{
  release: []
  open: [IOpenProject]
  openSaved: [{ id: string, name: string }]
}>()

const cartStore = useCartStore()
const indexStore = useAppIndexStore()
const pfinderAPI = usePfinderAPI()
const template = usePriceListTemplate(p.cart)
const { renameStored } = useProjectRename()

const accountId = computed((): string => ut_wilo_accountId())

// --- My Projects ------------------------------------------------------------------------------

const projects: Ref<TCartListItem[]> = ref([])
const confirmOpen: Ref<boolean> = ref(false)
const confirmTarget: Ref<TCartListItem | undefined> = ref()

// --- Wilo Templates ---------------------------------------------------------------------------

// The predefined structure, maintained by the customer in CMI and exported into the PriceList value
// list by scripting-wilo — every language in one JSON (descs), so a project taken over from it can
// switch language later. Root = the landscape, its children = the catalogs shown as tiles.
const priceList: Ref<IPriceListNode | undefined> = ref(undefined)
const templates: ComputedRef<IPriceListNode[]> = computed(() => priceList.value?.children || [])
// Set when a picked template resolved to nothing at all — see startFromTemplate.
const templateEmptyMsg: Ref<string> = ref('')
// Blocks a second click while the first one is still resolving and adding.
const busy: Ref<boolean> = ref(false)
/*
 * ⚑ One lock for all three ways in, this component's own work AND the cart page's (usePriceListProject#opening): two openings running
 * at once both write the one cart, and whichever answers last decides what the other project shows
 * and then saves.
 */
const blocked: ComputedRef<boolean> = computed(() => busy.value || p.opening)

onMounted(async () => {
  void refreshProjects()
  const res = await pfinderAPI.fetchData(ExtCmdType.EXP_PRICELIST, {}, {})
  priceList.value = res?.result
})

async function refreshProjects(): Promise<void> {
  if (!accountId.value) {
    projects.value = []
    return
  }
  const args: pfWilo.ExtCmd<ExtCmdType.CART_LIST>['arg'] = { groupId: accountId.value, type: p.cart }
  const res = await pfinderAPI.fetchData(ExtCmdType.CART_LIST, args, {})
  projects.value = (res && res.result) ? res.result : []
}

/*
 * The name a new project carries until someone renames it. Date and time are part of it on purpose:
 * five projects from the same template are otherwise indistinguishable on the tiles.
 */
function newProjectName(base: string): string {
  return base + ' – ' + new Date().toLocaleString()
}

// --- Opening ----------------------------------------------------------------------------------

// A saved project — only asked for here, Cart.vue loads and opens it (`openSaved`).
function openProject(item: TCartListItem): void {
  if (blocked.value) { return }
  emit('openSaved', { id: item.id, name: item.name || '' })
}

/*
 * A custom price list: nothing to resolve and editable from the first second. Confirming its name is
 * the moment it comes into being — `usePriceListProject#openProject` creates it on the server before it opens,
 * so it opens with its uuid in the address. That is why the name is asked here, at the start — see
 * `CartProjectNameModal`.
 *
 * ⚑ Only the question is asked here; the cart is emptied in `createEmpty`, AFTER the answer.
 * Emptying first would make Cancel destroy the cart of whoever changed their mind.
 */
const emptyAsk: Ref<boolean> = ref(false)
const emptySuggestion: Ref<string> = ref('')

function startEmpty(): void {
  if (blocked.value) { return }
  // Named after the tile it was started from, the same way a template project is named after its
  // template — the suggestion then says where the list came from.
  emptySuggestion.value = newProjectName(ut_getText('txt_pricelist-custom'))
  emptyAsk.value = true
}

function createEmpty(name: string): void {
  emptyAsk.value = false
  // Asked again at the answer: something may have started opening while the name was typed.
  if (blocked.value) { return }
  emit('release')
  cartStore.setEmptyCart(p.cart)
  emit('open', { id: '', name, editable: true })
}

/*
 * A template. The whole catalog goes into the cart, structure and series, in export order — and the
 * project starts LOCKED: it was handed over complete and is meant to be read first. "Enable edit"
 * opens it, and that is also the moment auto-save arms.
 */
async function startFromTemplate(picked: IPriceListNode): Promise<void> {
  if (blocked.value) { return }
  busy.value = true
  templateEmptyMsg.value = ''
  emit('release')
  cartStore.setEmptyCart(p.cart)

  if (await template.takeOver(picked)) {
    emit('open', { id: '', name: newProjectName(templateText(picked) || 'Project'), editable: false, templateName: templateText(picked), pdf: template.pdfDefaults(picked.pdf) })
  }
  // Nothing survived: not one series of this template is available here. Without a word the click
  // would simply look broken — the tiles stay and nothing happens.
  else { templateEmptyMsg.value = ut_getText('txt_pricelist-template-empty') }
  busy.value = false
}

// A template's text in the language the app shows — tile and suggested project name.
function templateText(node: IPriceListNode): string {
  return ut_wilo_templateText(node, indexStore.state.paramData.lcda)
}

// --- Renaming and deleting ---------------------------------------------------------------------

/*
 * Renaming on the tile — `CART_UPDATE` with only a name, the data stays untouched. The generated
 * name (template or "Custom price list" plus date) is a starting point, not a decision — and since
 * it is now confirmed in a modal before the project exists, often already the name someone chose.
 */
const renameId: Ref<string> = ref('')
const nameDraft: Ref<string> = ref('')

function startRename(item: TCartListItem): void {
  renameId.value = item.id
  nameDraft.value = item.name || ''
}

async function saveRename(item: TCartListItem): Promise<void> {
  const name: string = nameDraft.value.trim()
  renameId.value = ''
  if (!name || name === item.name) { return }
  // Failed or not, the list is read again: it shows what the server holds, which is the answer.
  await renameStored(item.id, name)
  await refreshProjects()
}

function askDelete(item: TCartListItem): void {
  confirmTarget.value = item
  confirmOpen.value = true
}

async function confirmDelete(): Promise<void> {
  const item = confirmTarget.value
  confirmOpen.value = false
  if (!item) { return }
  const args: pfWilo.ExtCmd<ExtCmdType.CART_DELETE>['arg'] = { uuid: item.id }
  await pfinderAPI.fetchData(ExtCmdType.CART_DELETE, args, {})
  await refreshProjects()
}

// SQLite CURRENT_TIMESTAMP is 'YYYY-MM-DD HH:MM:SS' in UTC — render it in the local locale.
function formatDate(dt: string): string {
  if (!dt) { return '' }
  const d = new Date(dt.replace(' ', 'T') + 'Z')
  return isNaN(d.getTime()) ? dt : d.toLocaleString()
}
</script>

<template>
  <div class="py-4">
    <!-- My Projects first (13.10.2026, Christoph): what this account has saved for this cart type. The
         custom price list closes this grid, so the grid stands even without a saved project; it reaches
         the server with its confirmed name. -->
    <h2 class="mb-4" v-html="ut_getText('txt_pricelist-my-projects')" />
    <div v-if="!projects.length" class="mb-4 text-sm" v-html="ut_getText('txt_pricelist-no-projects')" />
    <!-- Tiles in the layout of the series cards in List.vue: picture area on white, title with the arrow below. -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <UCard
        v-for="item in projects"
        :key="item.id"
        class="hover:bg-gunmetal-4"
        :class="blocked ? 'cursor-wait' : 'cursor-pointer'"
        @click="openProject(item)"
      >
        <!-- The cover the project saved for its pdf, as on the template tiles below; an uploaded cover
             (coverCustom) wins over the selected one. -->
        <CartTilePicture
          :src="ut_wilo_coverTilePicture(item.cover, item.coverCustom)"
          :fill="!!(item.coverCustom || item.cover)"
          :alt="item.name || ''"
        />
        <div class="px-4 py-8">
          <!-- @click.stop on the field as well: the tile opens the project, and typing a name is
               not opening it. Interpolated, not v-html — this text is typed by a user. -->
          <UInput
            v-if="renameId === item.id"
            v-model="nameDraft"
            autofocus
            class="w-full"
            @click.stop
            @blur="void saveRename(item)"
            @keyup.enter="void saveRename(item)"
          />
          <h4 v-else class="break-words">{{ item.name || ut_getText('txt_pricelist-no-name') }}</h4>
          <div class="text-xs mt-2" v-html="formatDate(item.dateUpdated)" />
        </div>
        <template #footer>
          <div class="flex justify-end gap-2 p-2">
            <!-- @click.stop: the tile itself opens the project, these two must not. -->
            <UButton
              icon="i-wilo-pen-solid-full"
              variant="ghost"
              color="tertiary"
              @click.stop="startRename(item)"
            />
            <UButton
              icon="i-wilo-trash-solid-full"
              variant="ghost"
              color="tertiary"
              @click.stop="askDelete(item)"
            />
          </div>
        </template>
      </UCard>

      <!-- The custom price list has no picture: an icon in the same picture area keeps the tiles in line. -->
      <UCard class="hover:bg-gunmetal-4" :class="blocked ? 'cursor-wait' : 'cursor-pointer'" @click="startEmpty()">
        <CartTilePicture icon="i-wilo-plus-solid-full" />
        <div class="px-4 py-8">
          <div class="flex justify-between items-center">
            <h4 v-html="ut_getText('txt_pricelist-custom')" />
            <div class="bg-gunmetal-4 group-hover:bg-primary pb-0,75 pt-1.5 px-1.5 rounded-lg">
              <UIcon name="i-wilo-chevron-right-solid-full" class="text-gunmetal group-hover:text-white size-5.5" />
            </div>
          </div>
        </div>
      </UCard>
    </div>

    <!-- Wilo Templates below the own projects: the predefined catalogs. A click hands the whole structure
         over; a template reaches the server with "Enable edit". -->
    <h2 class="mt-16 mb-4" v-html="ut_getText('txt_pricelist-templates')" />
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <UCard
        v-for="item in templates"
        :key="item.nodeName"
        class="hover:bg-gunmetal-4"
        :class="blocked ? 'cursor-wait' : 'cursor-pointer'"
        @click="void startFromTemplate(item)"
      >
        <CartTilePicture
          :src="ut_wilo_coverTilePicture(item.pdf?.cover)"
          :fill="!!item.pdf?.cover"
          :alt="templateText(item)"
        />
        <div class="px-4 py-8">
          <div class="flex justify-between items-center">
            <h4 v-html="templateText(item)" />
            <div class="bg-gunmetal-4 group-hover:bg-primary pb-0,75 pt-1.5 px-1.5 rounded-lg">
              <UIcon name="i-wilo-chevron-right-solid-full" class="text-gunmetal group-hover:text-white size-5.5" />
            </div>
          </div>
        </div>
      </UCard>
    </div>
    <div v-if="templateEmptyMsg" class="pt-4 text-sm" v-html="templateEmptyMsg" />

    <CartProjectNameModal
      :open="emptyAsk"
      :suggestion="emptySuggestion"
      @confirm="createEmpty($event)"
      @cancel="emptyAsk = false"
    />

    <!-- One text key for the whole dialog (btn_delete, 25.09.2026, Christoph): title and button say "Delete", the
         body names the project. -->
    <UModal v-model:open="confirmOpen" :title="ut_getText('btn_delete')" :ui="useAppConfig().modalFrame.auto">
      <template #body>
        <p class="font-bold">{{ confirmTarget?.name || '' }}?</p>
        <div class="flex justify-end gap-2 mt-6">
          <UButton variant="ghost" :label="ut_getText('btn_cancel')" @click="() => { confirmOpen = false }" />
          <UButton :label="ut_getText('btn_delete')" @click="confirmDelete" />
        </div>
      </template>
    </UModal>
  </div>
</template>
